import { cva, type VariantProps } from 'class-variance-authority'
import {
	Button as AriaButton,
	DropZone as AriaDropZone,
	type DropZoneProps as AriaDropZoneProps,
	type DragTypes,
	type DropItem,
	type DropOperation,
	FileTrigger as AriaFileTrigger,
	isFileDropItem,
} from 'react-aria-components'
import { cn } from '../../lib/cn'
import { focusRing } from '../../lib/focus-ring'
import { Icon } from '../../atoms/icon/icon'

/**
 * The Upload set (463:50583): Small = False (463:50572), a 265px zone with the
 * content 60px from the top, and Small = True (463:50582), the content centred
 * with 24px all round. Both are white with a 1px dashed Accent stroke and the
 * 16px radius, the 38x39.52 cloud-upload icon, the prompt in Body SemiBold with
 * "browse" in Accent, and the accepted types in Body Light dark.
 *
 * The stroke is drawn as an SVG overlay rather than a CSS border, for two reasons:
 * CSS `border-dashed` cannot set the dash pattern, and the Figma renders 4.5px
 * dashes 4.5px apart (`stroke-dash.upload`); and the Figma stroke sits inside the
 * frame, so it must not add to the size: Small is 145.52px tall and Small = False
 * starts its content 60px from the top. The Figma fits its dashes to each side,
 * which CSS cannot, so the pattern matches and its phase matches along the top.
 *
 * The Figma draws the zone at rest only. While files are dragged over it
 * (`data-drop-target`) it takes the Accent light fill, and keyboard focus takes
 * the kit's accent outline; neither is drawn in the Figma.
 */
export const uploadVariants = cva(
	[
		'relative flex w-full flex-col items-center rounded-atlas-lg bg-atlas-light font-atlas-sans data-drop-target:bg-atlas-accent-light',
		focusRing,
	],
	{
		variants: {
			small: {
				false: 'h-66.25 px-6 pt-15 pb-6',
				true: 'justify-center p-6',
			},
		},
		defaultVariants: { small: false },
	},
)

// The 1px stroke's centre line is 0.5px inside the box, so the rect is inset by
// half the stroke and its corner radius is the zone's 16px less that half.
const strokeStyle = {
	width: 'calc(100% - 1px)',
	height: 'calc(100% - 1px)',
	rx: 'calc(var(--atlas-radius-lg) - 0.5px)',
	strokeDasharray: 'var(--atlas-stroke-dash-upload)',
	strokeDashoffset: 'var(--atlas-stroke-dash-upload-offset)',
}

function DashedStroke() {
	return (
		<svg aria-hidden="true" className="pointer-events-none absolute inset-0 size-full">
			<rect x="0.5" y="0.5" className="fill-none stroke-atlas-accent stroke-1" style={strokeStyle} />
		</svg>
	)
}

export interface UploadProps
	extends Omit<AriaDropZoneProps, 'className' | 'children' | 'onDrop' | 'getDropOperation'>,
		VariantProps<typeof uploadVariants> {
	className?: string
	/** Called with the files dropped or picked; files of a type not accepted are left out. */
	onFilesSelected: (files: File[]) => void
	/** MIME types the zone accepts, e.g. `['image/png', 'image/jpeg', 'application/pdf']`; `image/*` matches a family. */
	acceptedFileTypes?: string[]
	/** Whether more than one file can be picked at once. */
	allowsMultiple?: boolean
	/** The text before the browse link. */
	prompt?: string
	browseLabel?: string
	/** The accepted types and size, e.g. "PNG, JPG or PDF (Multiple files, up to 50MB each)". */
	hint?: string
}

function accepts(type: string, accepted?: string[]): boolean {
	if (!accepted || accepted.length === 0) return true
	return accepted.some((pattern) => (pattern.endsWith('/*') ? type.startsWith(pattern.slice(0, -1)) : type === pattern))
}

// A drag's types are concrete MIME types, so a family pattern such as `image/*`
// cannot be checked against them; the drop itself filters by family.
function dropOperation(types: DragTypes, accepted?: string[]): DropOperation {
	if (!accepted || accepted.length === 0 || accepted.some((pattern) => pattern.endsWith('/*'))) return 'copy'
	return accepted.some((type) => types.has(type)) ? 'copy' : 'cancel'
}

/**
 * A drag-and-drop zone with a "browse" file picker, built on react-aria's
 * DropZone and FileTrigger: files arrive through `onFilesSelected` whichever way
 * they came in. The size limit is enforced by the screen; the zone only shows it.
 */
export function Upload({
	small,
	onFilesSelected,
	acceptedFileTypes,
	allowsMultiple = true,
	prompt = 'Drag & Drop your files here or ',
	browseLabel = 'browse',
	hint,
	className,
	'aria-label': ariaLabel = 'Upload files',
	...props
}: UploadProps) {
	const onDrop = async (event: { items: DropItem[] }) => {
		const items = event.items.filter(isFileDropItem).filter((item) => accepts(item.type, acceptedFileTypes))
		const files = await Promise.all((allowsMultiple ? items : items.slice(0, 1)).map((item) => item.getFile()))
		if (files.length > 0) onFilesSelected(files)
	}
	const onSelect = (list: FileList | null) => {
		const files = Array.from(list ?? []).filter((file) => accepts(file.type, acceptedFileTypes))
		if (files.length > 0) onFilesSelected(files)
	}
	return (
		<AriaDropZone
			{...props}
			aria-label={ariaLabel}
			onDrop={onDrop}
			getDropOperation={(types) => dropOperation(types, acceptedFileTypes)}
			className={cn(uploadVariants({ small }), className)}
		>
			<DashedStroke />
			<div className={cn('flex w-full flex-col items-center justify-center', small ? 'gap-2' : 'gap-4')}>
				<Icon name="cloud-upload" />
				<div className="flex w-full flex-col text-center">
					<p className="text-atlas-body-semibold text-atlas-dark">
						{prompt}
						<AriaFileTrigger acceptedFileTypes={acceptedFileTypes} allowsMultiple={allowsMultiple} onSelect={onSelect}>
							<AriaButton className={cn('cursor-pointer rounded-atlas-sm text-atlas-accent', focusRing)}>
								{browseLabel}
							</AriaButton>
						</AriaFileTrigger>
					</p>
					{hint ? <p className="text-atlas-body text-atlas-light-dark">{hint}</p> : null}
				</div>
			</div>
		</AriaDropZone>
	)
}
